import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Button,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Timer,
  CheckCircle,
  Cancel,
  NavigateBefore,
  NavigateNext,
  ViewWeek,
} from '@mui/icons-material';
import AnswerSheetDock from '../components/AnswerSheetDock';
import AnswerSheet from '../components/AnswerSheet';

const ExamPage = () => {
  const { area } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [sessionId, setSessionId] = useState(null); // ID de sesión del backend
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(3600); // 60 minutes
  const [loading, setLoading] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [viewMode, setViewMode] = useState('dock'); // 'dock' o 'side-by-side'
  const [showProgressAlert, setShowProgressAlert] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [isStartingNew, setIsStartingNew] = useState(false); // Bandera para prevenir guardado automático
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [savedProgressData, setSavedProgressData] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [reviewData, setReviewData] = useState([]);

  const areaNames = {
    'matematicas': 'Matemáticas',
    'lectura-critica': 'Lectura Crítica',
    'sociales': 'Sociales',
    'ciencias': 'Ciencias',
    'ingles': 'Inglés',
  };

  // Función para mezclar un array (algoritmo Fisher-Yates)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Función para obtener las preguntas
  const fetchQuestions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const requestedLimit = searchParams.get('limit') || 20;
      const topics = searchParams.get('topics') || '';
      
      const url = `${process.env.REACT_APP_API_URL}/api/exams/questions/${area}?limit=${requestedLimit}${topics ? `&topics=${topics}` : ''}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data && response.data.questions && response.data.questions.length > 0) {
        // Parsear options si vienen como string
        const parsedQuestions = response.data.questions.map(q => ({
          ...q,
          options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options
        }));
        
        // Las preguntas ya vienen aleatorizadas del backend
        setQuestions(parsedQuestions);
        setSessionId(response.data.sessionId); // Guardar el session ID
        // Desactivar la bandera después de cargar preguntas nuevas
        setIsStartingNew(false);
      } else {
        setQuestions([]);
        setSessionId(null);
        setIsStartingNew(false);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [area, searchParams]);

  // Cargar progreso guardado al iniciar
  useEffect(() => {
    // Buscar cualquier examen en progreso (de cualquier área)
    const allAreas = ['matematicas', 'lectura-critica', 'sociales', 'ciencias', 'ingles'];
    let foundProgress = null;
    let foundArea = null;
    
    const count = parseInt(searchParams.get('count')) || 5;
    
    for (const searchArea of allAreas) {
      const savedProgress = localStorage.getItem(`exam_progress_${searchArea}`);
      if (savedProgress) {
        foundProgress = JSON.parse(savedProgress);
        foundArea = searchArea;
        break; // Solo puede haber un examen a la vez
      }
    }
    
    // Validar que el progreso guardado sea del mismo número de preguntas
    if (foundProgress) {
      const savedCount = foundProgress.questionCount || 0;
      const isValidProgress = area === foundArea && savedCount === count;
      
      if (isValidProgress) {
        setSavedProgressData({ ...foundProgress, area: foundArea });
        setShowResumeDialog(true);
        setLoading(false);
      } else {
        // Si el número de preguntas no coincide, limpiar progreso inválido
        console.log('🧹 Limpiando progreso inválido: esperaba', count, 'preguntas, pero encontró', savedCount);
        localStorage.removeItem(`exam_progress_${foundArea}`);
        fetchQuestions();
      }
    } else {
      fetchQuestions();
    }
  }, [area, fetchQuestions, searchParams]);

  // Función para continuar con progreso guardado
  const handleResumeProgress = () => {
    const savedArea = savedProgressData?.area || area;
    
    // Si el examen guardado es de otra área, redirigir
    if (savedArea !== area) {
      navigate(`/exam/${savedArea}${window.location.search}`);
      return;
    }
    
    // Si es la misma área, restaurar el progreso
    if (savedProgressData) {
      const { savedAnswers, savedCurrentQuestion, savedTimeLeft } = savedProgressData;
      setAnswers(savedAnswers || {});
      setCurrentQuestion(savedCurrentQuestion || 0);
      setTimeLeft(savedTimeLeft || 3600);
      
      // Mostrar alerta de progreso restaurado
      const answeredCount = Object.keys(savedAnswers || {}).length;
      setProgressMessage(`Progreso anterior restaurado: ${answeredCount} pregunta${answeredCount !== 1 ? 's' : ''} respondida${answeredCount !== 1 ? 's' : ''}`);
      setShowProgressAlert(true);
    }
    setShowResumeDialog(false);
    setLoading(true);
    fetchQuestions();
  };

  // Función para iniciar nuevo examen
  const handleStartNew = () => {
    // Activar bandera para prevenir guardado automático
    setIsStartingNew(true);
    
    // Limpiar TODOS los exámenes en progreso (de cualquier área)
    const allAreas = ['matematicas', 'lectura-critica', 'sociales', 'ciencias', 'ingles'];
    allAreas.forEach(areaName => {
      localStorage.removeItem(`exam_progress_${areaName}`);
      localStorage.removeItem(`exam_review_${areaName}`); // Limpiar también reviewData
    });
    
    setSavedProgressData(null);
    setAnswers({});
    setCurrentQuestion(0);
    setTimeLeft(3600);
    setShowResumeDialog(false);
    setLoading(true);
    fetchQuestions();
  };

  // Guardar progreso automáticamente cada vez que cambie
  useEffect(() => {
    // No guardar si estamos iniciando un nuevo examen
    if (isStartingNew) return;
    
    if (!loading && !showResults && questions.length > 0) {
      const progressData = {
        savedAnswers: answers,
        savedCurrentQuestion: currentQuestion,
        savedTimeLeft: timeLeft,
        questionCount: questions.length, // Guardar el número de preguntas
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(`exam_progress_${area}`, JSON.stringify(progressData));
    }
  }, [answers, currentQuestion, timeLeft, area, loading, showResults, questions.length, isStartingNew]);

  // Limpiar progreso cuando se muestran los resultados
  useEffect(() => {
    if (showResults) {
      localStorage.removeItem(`exam_progress_${area}`);
    }
  }, [showResults, area]);

  // Limpiar reviewData cuando se navega al dashboard
  useEffect(() => {
    return () => {
      // Limpiar reviewData al desmontar el componente
      const allAreas = ['matematicas', 'lectura-critica', 'sociales', 'ciencias', 'ingles'];
      allAreas.forEach(areaName => {
        localStorage.removeItem(`exam_review_${areaName}`);
      });
    };
  }, []);

  // Cargar reviewData cuando se abre la retrospección
  useEffect(() => {
    if (showReview && reviewData.length === 0) {
      console.log('🔄 showReview cambió a true, intentando cargar reviewData...');
      const savedReview = localStorage.getItem(`exam_review_${area}`);
      if (savedReview) {
        const parsed = JSON.parse(savedReview);
        console.log('✅ reviewData cargado en useEffect:', parsed.length, 'preguntas');
        setReviewData(parsed);
      } else {
        console.log('❌ No hay reviewData guardado para:', area);
      }
    }
  }, [showReview, reviewData.length, area]);

  // Limpiar progreso cuando el componente se desmonta (por si acaso)
  useEffect(() => {
    return () => {
      if (showResults) {
        localStorage.removeItem(`exam_progress_${area}`);
      }
    };
  }, [showResults, area]);

  useEffect(() => {
    if (loading || showResults) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loading, showResults]);

  const handleAnswerChange = (answer) => {
    setAnswers({
      ...answers,
      [currentQuestion]: answer,
    });
  };

  const handleSubmit = async () => {
    console.log('🚀 handleSubmit INICIADO');
    console.log('📊 Respuestas actuales (answers):', answers);
    console.log('📝 Preguntas totales:', questions.length);
    
    try {
      // Transformar respuestas de índice a ID de pregunta
      const formattedAnswers = {};
      Object.keys(answers).forEach((index) => {
        const question = questions[parseInt(index)];
        if (question) {
          formattedAnswers[question.id] = answers[index];
        }
      });

      console.log('📤 Enviando respuestas:', formattedAnswers);
      console.log('🔍 Preguntas con correct_answer:', questions.map(q => ({ 
        id: q.id, 
        correct_answer: q.correct_answer 
      })));
      console.log('🔍 Primera pregunta completa:', questions[0]);
      console.log('🔍 IDs que se envían:', Object.keys(formattedAnswers));

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/exams/submit`,
        {
          area,
          answers: formattedAnswers,
          timeSpent: 3600 - timeLeft,
          sessionId: sessionId // Incluir el session ID
        }
      );
      
      const reviewDataReceived = response.data.reviewData || [];
      
      console.log('� ReviewData recibido del backend:', reviewDataReceived);
      
      // Ya no necesitamos mapear nada porque el backend ya manejó todo
      // Las opciones y correct_answer ya están en el orden correcto
      setResults(response.data);
      setReviewData(reviewDataReceived);
      
      // Guardar reviewData en localStorage para la retrospección
      if (reviewDataReceived.length > 0) {
        localStorage.setItem(`exam_review_${area}`, JSON.stringify(reviewDataReceived));
      }
      
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting exam:', error);
      // Aún si hay error, mostrar algo al usuario
      alert('Hubo un error al enviar el examen. Por favor intenta de nuevo.');
    } finally {
      // SIEMPRE limpiar progreso guardado al finalizar (aunque haya error)
      localStorage.removeItem(`exam_progress_${area}`);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // No mostrar error si el diálogo de reanudar está abierto
  if (questions.length === 0 && !showResumeDialog) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No hay preguntas disponibles para esta área
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/dashboard')}
            >
              Volver al Dashboard
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Si el diálogo está abierto, solo renderizar el diálogo
  if (showResumeDialog) {
    const savedArea = savedProgressData?.area || area;
    const isSameArea = savedArea === area;
    
    return (
      <Dialog
        open={showResumeDialog}
        onClose={() => {}} // No permitir cerrar sin elegir
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: isSameArea ? 'warning.light' : 'error.light', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timer sx={{ fontSize: 32 }} />
            <Typography variant="h6">
              {isSameArea ? 'Examen en Progreso' : '⚠️ Ya tienes un examen abierto'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {isSameArea ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Tienes un examen de <strong>{areaNames[savedArea]}</strong> que no has terminado.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Ya tienes un examen de <strong>{areaNames[savedArea]}</strong> abierto.
              <br />
              Solo puedes tener un examen activo a la vez.
              <br /><br />
              ¿Quieres abandonar el examen de <strong>{areaNames[savedArea]}</strong> y comenzar uno nuevo de <strong>{areaNames[area]}</strong>?
            </Alert>
          )}
          {savedProgressData && (
            <Box sx={{ pl: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                📊 <strong>Preguntas respondidas:</strong> {Object.keys(savedProgressData.savedAnswers || {}).length}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                ⏱️ <strong>Tiempo restante:</strong> {formatTime(savedProgressData.savedTimeLeft || 3600)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                💾 Guardado el: {savedProgressData.savedAt ? new Date(savedProgressData.savedAt).toLocaleString('es-ES') : 'Desconocido'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          {isSameArea ? (
            <>
              <Button
                onClick={handleStartNew}
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
              >
                Iniciar Nuevo
              </Button>
              <Button
                onClick={handleResumeProgress}
                variant="contained"
                color="primary"
                startIcon={<CheckCircle />}
              >
                Continuar
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  // Volver a la página del examen guardado
                  navigate(`/exam/${savedArea}${window.location.search}`);
                  setShowResumeDialog(false);
                }}
                variant="outlined"
                color="primary"
              >
                Ir a mi examen de {areaNames[savedArea]}
              </Button>
              <Button
                onClick={handleStartNew}
                variant="contained"
                color="error"
                startIcon={<Cancel />}
              >
                Abandonar y empezar nuevo
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const question = questions[currentQuestion];

  // Convertir opciones de texto a letras A, B, C, D
  const optionLabels = ['A', 'B', 'C', 'D'];

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh', pb: { xs: 8, md: 0 } }}>
      {/* Barra de información del examen (no AppBar para evitar duplicados) */}
      <Paper elevation={3} sx={{ bgcolor: '#1a237e', color: 'white', borderRadius: 0 }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Examen Saber 11 - {areaNames[area]}
          </Typography>
          
          {/* Botón de cambio de modo */}
          <IconButton
            color="inherit"
            onClick={() => setViewMode(viewMode === 'dock' ? 'side-by-side' : 'dock')}
            sx={{ mr: 2 }}
            title={viewMode === 'dock' ? 'Cambiar a vista lado a lado' : 'Cambiar a vista dock'}
          >
            <ViewWeek />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timer />
            <Typography variant="h6">{formatTime(timeLeft)}</Typography>
          </Box>
        </Toolbar>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 6 }} />
      </Paper>

      {/* Dock flotante (solo en modo dock) */}
      {viewMode === 'dock' && (
        <AnswerSheetDock
          totalQuestions={questions.length}
          answers={answers}
          onAnswerSelect={(index, answer) => {
            setAnswers({ ...answers, [index]: answer });
          }}
          currentQuestion={currentQuestion}
          onQuestionNavigate={setCurrentQuestion}
        />
      )}

      <Container 
        maxWidth={viewMode === 'dock' ? 'md' : 'lg'} 
        sx={{ mt: 3, mb: 4, pr: { xs: 2, md: viewMode === 'dock' ? 2 : 2 } }}
      >
        {viewMode === 'side-by-side' ? (
          // Vista lado a lado (modo original)
          <Grid container spacing={3}>
            {/* Columna izquierda: Pregunta */}
            <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, bgcolor: 'white' }}>
              {/* Header de pregunta */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip 
                  label={`Pregunta ${currentQuestion + 1} de ${questions.length}`} 
                  color="primary" 
                  size="medium"
                />
                <Chip 
                  label={question.area || areaNames[area]} 
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Contexto de la pregunta (si existe) */}
              {(question?.context_text || question?.context_image) && (
                <Box sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: 4, borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                    📖 CONTEXTO
                  </Typography>
                  
                  {question.context_image && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <img 
                        src={question.context_image} 
                        alt="Contexto"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '300px',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                      />
                    </Box>
                  )}
                  
                  {question.context_text && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                        color: '#333'
                      }}
                    >
                      {question.context_text}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Texto de la pregunta */}
              <Box sx={{ mb: 4, minHeight: '120px' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 500,
                    lineHeight: 1.6,
                    color: '#333'
                  }}
                >
                  {question.question}
                </Typography>
              </Box>

              {/* Opciones con letras A, B, C, D */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Seleccione la respuesta correcta:
                </Typography>
                
                {question.options.map((option, index) => {
                  const letter = optionLabels[index];
                  const isSelected = answers[currentQuestion] === letter;
                  
                  return (
                    <Paper
                      key={index}
                      elevation={isSelected ? 3 : 0}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'grey.300',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        bgcolor: isSelected ? 'primary.50' : 'white',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateX(4px)',
                        },
                      }}
                      onClick={() => handleAnswerChange(letter)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            border: 2,
                            borderColor: isSelected ? 'primary.main' : 'grey.400',
                            bgcolor: isSelected ? 'primary.main' : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Typography 
                            variant="body1" 
                            fontWeight="bold"
                            color={isSelected ? 'white' : 'text.primary'}
                          >
                            {letter}
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ flex: 1 }}>
                          {option}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>

              {/* Navegación */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  size="large"
                  disabled={currentQuestion === 0}
                  onClick={() => setCurrentQuestion(currentQuestion - 1)}
                  startIcon={<NavigateBefore />}
                >
                  Anterior
                </Button>
                <Box>
                  {currentQuestion === questions.length - 1 ? (
                    <Button
                      variant="contained"
                      size="large"
                      color="success"
                      onClick={handleSubmit}
                      disabled={Object.keys(answers).length !== questions.length}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Finalizar Examen
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setCurrentQuestion(currentQuestion + 1)}
                      endIcon={<NavigateNext />}
                    >
                      Siguiente
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Columna derecha: Hoja de respuestas estilo ICFES */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 2, 
                position: 'sticky', 
                top: 16,
                bgcolor: 'white',
                border: 2,
                borderColor: '#333'
              }}
            >
              <Box sx={{ mb: 2, textAlign: 'center', bgcolor: '#1a237e', color: 'white', py: 1, borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                  HOJA DE RESPUESTAS
                </Typography>
                <Typography variant="caption">
                  Marca solo una opción
                </Typography>
              </Box>

              {/* Grid de respuestas */}
              <Box sx={{ maxHeight: '70vh', overflowY: 'auto', pr: 1 }}>
                {questions.map((q, index) => {
                  const isActive = currentQuestion === index;
                  const hasAnswer = answers[index] !== undefined;
                  
                  return (
                    <Box
                      key={index}
                      onClick={() => setCurrentQuestion(index)}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        bgcolor: isActive ? '#e3f2fd' : hasAnswer ? '#f5f5f5' : 'white',
                        border: 2,
                        borderColor: isActive ? '#1976d2' : hasAnswer ? '#4caf50' : '#ddd',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: '#f5f5f5',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <AnswerSheet
                        questionNumber={index + 1}
                        selectedAnswer={answers[index]}
                        onAnswerSelect={(answer) => {
                          setAnswers({ ...answers, [index]: answer });
                          setCurrentQuestion(index);
                        }}
                        disabled={false}
                      />
                    </Box>
                  );
                })}
              </Box>

              {/* Leyenda */}
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: 2, borderColor: '#ddd', bgcolor: 'white' }} />
                    <Typography variant="caption">Sin marcar</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#333' }} />
                    <Typography variant="caption">Marcada</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#1976d2' }} />
                    <Typography variant="caption">Actual</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Resumen */}
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" align="center">
                  <strong>{Object.keys(answers).length}</strong> de <strong>{questions.length}</strong> respondidas
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        ) : (
          // Vista dock (solo pregunta centrada)
          <Paper elevation={3} sx={{ p: 3, bgcolor: 'white', maxWidth: '800px', mx: 'auto' }}>
            {/* Header de pregunta */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Chip 
                label={`Pregunta ${currentQuestion + 1} de ${questions.length}`} 
                color="primary" 
                size="medium"
              />
              <Chip 
                label={question.area || areaNames[area]} 
                variant="outlined"
                size="small"
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Contexto de la pregunta (si existe) */}
            {(question?.context_text || question?.context_image) && (
              <Box sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: 4, borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                  📖 CONTEXTO
                </Typography>
                
                {question.context_image && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <img 
                      src={question.context_image} 
                      alt="Contexto"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  </Box>
                )}
                
                {question.context_text && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                      color: '#333'
                    }}
                  >
                    {question.context_text}
                  </Typography>
                )}
              </Box>
            )}

            {/* Texto de la pregunta */}
            <Box sx={{ mb: 4, minHeight: '100px' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 500,
                  lineHeight: 1.6,
                  color: '#333'
                }}
              >
                {question.question}
              </Typography>
            </Box>

            {/* Opciones con letras A, B, C, D */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Seleccione la respuesta correcta:
              </Typography>
              
              {question.options.map((option, index) => {
                const letter = optionLabels[index];
                const isSelected = answers[currentQuestion] === letter;
                
                return (
                  <Paper
                    key={index}
                    elevation={isSelected ? 3 : 0}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: 2,
                      borderColor: isSelected ? 'primary.main' : 'grey.300',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      bgcolor: isSelected ? 'primary.50' : 'white',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateX(4px)',
                      },
                    }}
                    onClick={() => handleAnswerChange(letter)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          border: 2,
                          borderColor: isSelected ? 'primary.main' : 'grey.400',
                          bgcolor: isSelected ? 'primary.main' : 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Typography 
                          variant="body1" 
                          fontWeight="bold"
                          color={isSelected ? 'white' : 'text.primary'}
                        >
                          {letter}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {option}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Box>

            {/* Navegación */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Button
                variant="outlined"
                size="large"
                disabled={currentQuestion === 0}
                onClick={() => setCurrentQuestion(currentQuestion - 1)}
                startIcon={<NavigateBefore />}
              >
                Anterior
              </Button>
              <Box>
                {currentQuestion === questions.length - 1 ? (
                  <Button
                    variant="contained"
                    size="large"
                    color="success"
                    onClick={handleSubmit}
                    disabled={Object.keys(answers).length !== questions.length}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Finalizar Examen
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setCurrentQuestion(currentQuestion + 1)}
                    endIcon={<NavigateNext />}
                  >
                    Siguiente
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Container>

      {/* Results Dialog */}
      <Dialog open={showResults} maxWidth="sm" fullWidth>
        <DialogTitle>Resultados del Examen</DialogTitle>
        <DialogContent>
          {results && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" color="primary" gutterBottom>
                {results.score}%
              </Typography>
              <Typography variant="h6" gutterBottom>
                {results.correct} de {results.total} correctas
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 4 }}>
                <Box>
                  <CheckCircle color="success" sx={{ fontSize: 40 }} />
                  <Typography>Correctas: {results.correct}</Typography>
                </Box>
                <Box>
                  <Cancel color="error" sx={{ fontSize: 40 }} />
                  <Typography>Incorrectas: {results.incorrect}</Typography>
                </Box>
              </Box>
              {results.tips && results.tips.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Tips para mejorar:
                  </Typography>
                  {results.tips.map((tip, index) => (
                    <Chip key={index} label={tip} sx={{ m: 0.5 }} />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              console.log('🔍 Verificando reviewData antes de abrir retrospección:', reviewData);
              console.log('🔍 reviewData.length:', reviewData.length);
              
              // Limpiar progreso del examen cuando abres la retrospección
              localStorage.removeItem(`exam_progress_${area}`);
              
              // Cargar reviewData de localStorage si no está disponible
              let dataToShow = reviewData;
              if (dataToShow.length === 0) {
                console.log('⚠️ reviewData está vacío, intentando cargar de localStorage...');
                const savedReview = localStorage.getItem(`exam_review_${area}`);
                console.log('💾 Datos guardados en localStorage:', savedReview);
                if (savedReview) {
                  dataToShow = JSON.parse(savedReview);
                  setReviewData(dataToShow);
                  console.log('✅ reviewData cargado de localStorage:', dataToShow.length, 'preguntas');
                } else {
                  console.log('❌ No hay datos guardados en localStorage');
                }
              } else {
                console.log('✅ reviewData ya está disponible:', dataToShow.length, 'preguntas');
              }
              
              setShowResults(false);
              setShowReview(true);
            }} 
            variant="outlined"
            color="primary"
          >
            Ver Retrospección
          </Button>
          <Button onClick={() => navigate('/dashboard')} variant="contained">
            Volver al Dashboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog (Retrospección) */}
      <Dialog 
        open={showReview} 
        fullScreen
        onClose={() => {
          setShowReview(false);
          navigate('/dashboard');
        }}
      >
        <Paper elevation={3} sx={{ bgcolor: '#1a237e', color: 'white', borderRadius: 0 }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                setShowReview(false);
                navigate('/dashboard');
              }}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Retrospección - {areaNames[area]}
            </Typography>
            {results && (
              <Chip 
                label={`${results.score}% - ${results.correct}/${results.total}`}
                color={results.score >= 70 ? "success" : results.score >= 50 ? "warning" : "error"}
                sx={{ mr: 2 }}
              />
            )}
          </Toolbar>
        </Paper>

        <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
          {reviewData.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay datos de retrospección disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Por favor, completa el examen de nuevo para ver la retrospección
              </Typography>
              <Button 
                variant="contained" 
                sx={{ mt: 2 }}
                onClick={() => {
                  setShowReview(false);
                  navigate('/dashboard');
                }}
              >
                Volver al Dashboard
              </Button>
            </Paper>
          ) : (
            <>
              {reviewData.map((item, index) => (
            <Paper key={item.id} elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
              {/* Cabecera de la pregunta */}
              <Box 
                sx={{ 
                  bgcolor: item.isCorrect ? 'success.main' : 'error.main',
                  color: 'white',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {item.isCorrect ? (
                    <CheckCircle sx={{ fontSize: 28 }} />
                  ) : (
                    <Cancel sx={{ fontSize: 28 }} />
                  )}
                  <Typography variant="h6">
                    Pregunta {index + 1} de {reviewData.length}
                  </Typography>
                </Box>
                <Chip 
                  label={item.topic}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Box>

              {/* Contenido de la pregunta */}
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                  {item.question}
                </Typography>

                {/* Opciones con estilo de hoja de respuestas */}
                <Box sx={{ mt: 3 }}>
                  {item.options.map((option, optIndex) => {
                    const optionLetter = ['A', 'B', 'C', 'D'][optIndex];
                    const isUserAnswer = optionLetter === item.userAnswer;
                    const isCorrectAnswer = optionLetter === item.correctAnswer;
                    
                    // Determinar colores basado en la lógica correcta
                    let bgcolor = 'white';
                    let borderColor = '#ddd';
                    
                    // Si es la respuesta correcta, siempre verde
                    if (isCorrectAnswer) {
                      bgcolor = '#e8f5e9'; // Verde claro
                      borderColor = '#4caf50';
                    }
                    
                    // Si es la respuesta del usuario Y está incorrecta, rojo (sobrescribe el verde)
                    if (isUserAnswer && !item.isCorrect) {
                      bgcolor = '#ffebee'; // Rojo claro
                      borderColor = '#f44336';
                    }

                    return (
                      <Box
                        key={optIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 2,
                          mb: 2,
                          border: 3,
                          borderColor: borderColor,
                          borderRadius: 2,
                          bgcolor: bgcolor,
                          position: 'relative',
                          transition: 'all 0.3s'
                        }}
                      >
                        {/* Óvalo con letra */}
                        <Box
                          sx={{
                            width: 45,
                            height: 45,
                            minWidth: 45,
                            borderRadius: '50%',
                            border: 3,
                            borderColor: borderColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            bgcolor: isUserAnswer || isCorrectAnswer ? bgcolor : 'white',
                            color: isCorrectAnswer ? '#2e7d32' : (isUserAnswer && !item.isCorrect) ? '#c62828' : '#000'
                          }}
                        >
                          {optionLetter}
                        </Box>

                        {/* Texto de la opción */}
                        <Typography 
                          sx={{ 
                            flex: 1,
                            fontWeight: isUserAnswer || isCorrectAnswer ? 600 : 400
                          }}
                        >
                          {option}
                        </Typography>

                        {/* Íconos y etiquetas */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                          {isCorrectAnswer && (
                            <>
                              <CheckCircle sx={{ color: '#4caf50', fontSize: 28 }} />
                              <Chip 
                                label="Correcta" 
                                size="small" 
                                sx={{ 
                                  bgcolor: '#4caf50', 
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            </>
                          )}
                          {isUserAnswer && !item.isCorrect && (
                            <>
                              <Cancel sx={{ color: '#f44336', fontSize: 28 }} />
                              <Chip 
                                label="Tu respuesta" 
                                size="small" 
                                sx={{ 
                                  bgcolor: '#f44336', 
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            </>
                          )}
                          {isUserAnswer && item.isCorrect && (
                            <Chip 
                              label="Tu respuesta" 
                              size="small" 
                              sx={{ 
                                bgcolor: '#4caf50', 
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Nota explicativa si es incorrecta */}
                {!item.isCorrect && (
                  <Box 
                    sx={{ 
                      mt: 3, 
                      p: 2, 
                      bgcolor: '#fff3e0', 
                      borderLeft: 4,
                      borderColor: '#ff9800',
                      borderRadius: 1 
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#e65100' }}>
                      {item.correctAnswer 
                        ? `💡 Recordatorio: La respuesta correcta era la opción ${item.correctAnswer}`
                        : '⚠️ No se pudo determinar la respuesta correcta para esta pregunta (error en la base de datos)'
                      }
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          ))}

          {/* Botón para volver */}
          <Box sx={{ textAlign: 'center', mt: 4, mb: 4 }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/dashboard')}
              sx={{ minWidth: 200 }}
            >
              Volver al Dashboard
            </Button>
          </Box>
          </>
          )}
        </Container>
      </Dialog>

      {/* Snackbar para mostrar progreso restaurado */}
      <Snackbar
        open={showProgressAlert}
        autoHideDuration={5000}
        onClose={() => setShowProgressAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowProgressAlert(false)} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          {progressMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExamPage;

